/* eslint-disable i18next/no-literal-string */

import { cx, css } from '@lastbrand/web-ui-styled-system/css'

import { Button } from '@src/quince-components/src'
import ButtonDomain from '@src/quince-site-common/src/domains/Button'
import type { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import type RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { ButtonCTAType } from '@src/types/button-cta.type'
import { navigateToB2BSelfServe } from '@src/utils/navigate-to-b2b'

import {
  classnameArraysToDSStyle,
  dynamicValueClassesToInlineStyle,
  ivyDecorativeTextStyle,
  mergeLegacyResponsiveStyles,
} from './classname-to-ds-style'
import { RichTextDS } from './rich-text-ds'
import styles from './rich-text-ds-dynamic-override.module.css'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'
import type { MouseEvent } from 'react'

const SECTION_LAYOUT_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}

export interface RichTextDSRendererProps {
  id?: string
  title?: string
  sectionType?: string
  richTexts?: RichTextDomain[]
  buttons?: ButtonDomain[]
  classNames?: string[]
  baseDynamicClasses?: string[]
  mobileDynamicClassesOverrides?: string[]
  trackingMetaData?: TrackingMetaData
  bringInViewHash?: string
  testId?: string
  renderEmpty?: boolean
}

function hasStyle(style: SystemStyleObject): boolean {
  return Object.keys(style).length > 0
}

/**
 * Shared DS renderer for RichText surfaces that match the legacy `RichTextSection`
 * flow: centered stacked rich text blocks followed by optional CTA buttons.
 */
export function RichTextDSRenderer({
  id,
  title,
  sectionType,
  richTexts = [],
  buttons = [],
  classNames = [],
  baseDynamicClasses = [],
  mobileDynamicClassesOverrides = [],
  trackingMetaData,
  bringInViewHash,
  testId = 'rich-text-ds-renderer',
  renderEmpty = false,
}: RichTextDSRendererProps) {
  if (!renderEmpty && richTexts.length === 0 && buttons.length === 0) return null

  const sectionClassStyle = classnameArraysToDSStyle(classNames)
  const sectionStyle = mergeLegacyResponsiveStyles(
    sectionClassStyle,
    SECTION_LAYOUT_STYLE,
    // Match common.scss order: default column/center layout beats row/space-between, but `d-none` beats `d-flex`.
    { display: sectionClassStyle.display ?? SECTION_LAYOUT_STYLE.display },
  )
  const sectionDynamic = dynamicValueClassesToInlineStyle(baseDynamicClasses, mobileDynamicClassesOverrides, 'section')

  return (
    <div
      data-bring-in-view={bringInViewHash}
      className={cx(styles.mobileMaxWidthOverride, css(sectionStyle), sectionDynamic.className)}
      style={sectionDynamic.style}
      data-testid={testId}
    >
      <div className={css({ display: 'flex', flexDirection: 'column', alignItems: 'center' })}>
        {richTexts.map((richText, idx) => {
          const baseStyle = ivyDecorativeTextStyle(
            classnameArraysToDSStyle(richText.commonClassNames, richText.mobileClassNames),
          )
          const richTextDynamic = dynamicValueClassesToInlineStyle(
            richText.baseDynamicValueClasses,
            richText.dynamicValueClassesMobile,
          )

          return (
            <RichTextDS
              key={richText.id || idx}
              content={richText.raw}
              baseStyle={hasStyle(baseStyle) ? baseStyle : undefined}
              className={richTextDynamic.className}
              style={richTextDynamic.style}
            />
          )
        })}
        {buttons.map(button => {
          const buttonProps = ButtonDomain.makeButtonProps(button)
          if (!buttonProps) return null

          const buttonNavigationUrl =
            (button.link as (typeof button.link & { url?: string }) | undefined)?.url ?? button.link?.navigationPath
          const isB2BSelfServe = button.ctaType === ButtonCTAType.B2B_SELF_SERVE && !!buttonNavigationUrl

          return (
            <Button
              key={buttonProps.id}
              {...buttonProps}
              href={isB2BSelfServe ? undefined : buttonProps.href}
              onClick={
                isB2BSelfServe
                  ? (e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation()
                      navigateToB2BSelfServe(buttonNavigationUrl)
                    }
                  : buttonProps.onClick
              }
              trackConfig={{
                trackInternal: true,
                category: '',
                action: '',
                context: {
                  ...trackingMetaData?.context,
                  label: `${title}__CTA`,
                  sectionType,
                  sectionId: id,
                },
              }}
            />
          )
        })}
      </div>
    </div>
  )
}
/* eslint-enable i18next/no-literal-string */
